"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Accessibility,
  AlignJustify,
  Check,
  Contrast,
  Eye,
  Link2,
  MousePointer2,
  PauseCircle,
  RotateCcw,
  Type,
  X,
  Keyboard,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePrefs } from "./prefs-provider";

export const A11Y_KEY = "chatisafe-a11y";

export type A11yState = {
  /** 100 = default, 112 / 125 / 137 / 150 percent */
  fontScale: number;
  contrast: boolean;
  grayscale: boolean;
  links: boolean;
  readable: boolean;
  spacing: boolean;
  motion: boolean;
  cursor: boolean;
  focus: boolean;
};

export const A11Y_DEFAULT: A11yState = {
  fontScale: 100,
  contrast: false,
  grayscale: false,
  links: false,
  readable: false,
  spacing: false,
  motion: false,
  cursor: false,
  focus: false,
};

const SCALES = [100, 112, 125, 137, 150];

const FLAG_CLASS: Record<Exclude<keyof A11yState, "fontScale">, string> = {
  contrast: "a11y-contrast",
  grayscale: "a11y-gray",
  links: "a11y-links",
  readable: "a11y-readable",
  spacing: "a11y-spacing",
  motion: "a11y-no-motion",
  cursor: "a11y-cursor",
  focus: "a11y-focus",
};

export function applyA11y(s: A11yState) {
  const root = document.documentElement;
  root.style.fontSize = s.fontScale === 100 ? "" : `${s.fontScale}%`;
  (Object.keys(FLAG_CLASS) as (keyof typeof FLAG_CLASS)[]).forEach((k) => {
    root.classList.toggle(FLAG_CLASS[k], !!s[k]);
  });
}

function load(): A11yState {
  try {
    const raw = localStorage.getItem(A11Y_KEY);
    if (!raw) return A11Y_DEFAULT;
    return { ...A11Y_DEFAULT, ...(JSON.parse(raw) as Partial<A11yState>) };
  } catch {
    return A11Y_DEFAULT;
  }
}

export function AccessibilityMenu({ compact = false }: { compact?: boolean }) {
  const { t } = usePrefs();
  const a = t.a11y;
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<A11yState>(A11Y_DEFAULT);
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const s = load();
    setState(s);
    applyA11y(s);
  }, []);

  const update = useCallback((patch: Partial<A11yState>) => {
    setState((cur) => {
      const next = { ...cur, ...patch };
      localStorage.setItem(A11Y_KEY, JSON.stringify(next));
      applyA11y(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(A11Y_KEY);
    setState(A11Y_DEFAULT);
    applyA11y(A11Y_DEFAULT);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const scaleIdx = Math.max(0, SCALES.indexOf(state.fontScale));
  const active =
    state.fontScale !== 100 ||
    (Object.keys(FLAG_CLASS) as (keyof typeof FLAG_CLASS)[]).some((k) => state[k]);

  const toggles: { key: keyof typeof FLAG_CLASS; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: "contrast", label: a.contrast, icon: Contrast },
    { key: "grayscale", label: a.grayscale, icon: Palette },
    { key: "links", label: a.links, icon: Link2 },
    { key: "readable", label: a.readable, icon: Type },
    { key: "spacing", label: a.spacing, icon: AlignJustify },
    { key: "motion", label: a.motion, icon: PauseCircle },
    { key: "cursor", label: a.cursor, icon: MousePointer2 },
    { key: "focus", label: a.focus, icon: Keyboard },
  ];

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex w-9 items-center justify-center rounded-xl border bg-surface transition-colors hover:bg-surface-2",
          compact ? "h-9" : "h-10",
          active ? "border-brand text-brand" : "border-line text-ink",
        )}
        title={a.button}
        aria-label={a.button}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="a11y-panel"
      >
        <Accessibility className="h-4 w-4" aria-hidden />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            id="a11y-panel"
            role="dialog"
            aria-label={a.title}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 top-20 z-[70] max-h-[75svh] origin-top overflow-y-auto rounded-2xl border border-line bg-surface p-4 shadow-3 sm:absolute sm:inset-x-auto sm:end-0 sm:top-12 sm:w-80"
          >
            <div className="flex items-center justify-between">
              <p className="font-display text-base font-bold text-ink">{a.title}</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="-me-1.5 rounded-lg p-2 text-ink-2 hover:bg-surface-2 hover:text-ink"
                aria-label={a.close}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="mt-3 rounded-xl bg-surface-2 p-3">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
                  <Type className="h-4 w-4" aria-hidden /> {a.fontSize}
                </span>
                <span className="tabular text-xs text-ink-2" aria-live="polite">
                  {state.fontScale}%
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => update({ fontScale: SCALES[Math.max(0, scaleIdx - 1)] })}
                  disabled={scaleIdx === 0}
                  className="flex h-10 flex-1 items-center justify-center rounded-lg border border-line bg-surface text-sm font-bold text-ink hover:bg-surface-2 disabled:opacity-40"
                  aria-label={a.decrease}
                >
                  A−
                </button>
                <button
                  type="button"
                  onClick={() => update({ fontScale: SCALES[Math.min(SCALES.length - 1, scaleIdx + 1)] })}
                  disabled={scaleIdx === SCALES.length - 1}
                  className="flex h-10 flex-1 items-center justify-center rounded-lg border border-line bg-surface text-lg font-bold text-ink hover:bg-surface-2 disabled:opacity-40"
                  aria-label={a.increase}
                >
                  A+
                </button>
              </div>
            </div>

            <ul className="mt-3 grid grid-cols-2 gap-2">
              {toggles.map(({ key, label, icon: Icon }) => {
                const on = !!state[key];
                return (
                  <li key={key}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      onClick={() => update({ [key]: !on } as Partial<A11yState>)}
                      className={cn(
                        "flex h-full min-h-[3.75rem] w-full flex-col items-start gap-1.5 rounded-xl border p-3 text-start text-xs font-semibold transition-colors",
                        on ? "border-brand bg-brand text-on-brand" : "border-line bg-surface text-ink hover:border-line-strong hover:bg-surface-2",
                      )}
                    >
                      <span className="flex w-full items-center justify-between">
                        <Icon className="h-4 w-4" aria-hidden />
                        {on ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                      </span>
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={reset}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden /> {a.reset}
              </button>
              <Link
                href="/accessibility"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 items-center gap-1.5 px-2 text-xs font-semibold text-brand underline-offset-2 hover:underline"
              >
                <Eye className="h-3.5 w-3.5" aria-hidden /> {a.statement}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Keyboard users: first tab stop jumps past the header. */
export function SkipLink() {
  const { t } = usePrefs();
  return (
    <a
      href="#main-content"
      className="skip-link"
      onClick={(e) => {
        const target =
          document.getElementById("main-content") ||
          document.querySelector("main") ||
          document.querySelector("section");
        if (target instanceof HTMLElement) {
          e.preventDefault();
          target.setAttribute("tabindex", "-1");
          target.focus({ preventScroll: false });
          target.scrollIntoView({ block: "start" });
        }
      }}
    >
      {t.a11y.skip}
    </a>
  );
}
